import { observable, action, computed, toJS } from 'mobx';
import { AxiosResponse } from 'axios';

import Services from '@services/index';
import { JrpcResponse } from '@httpClient/jrpc';
import {
  ShutdownReasonInterface,
  ShutdownReason,
  LoadShutdownReason,
} from '@core/entities/Opencity/ShutdownReason';
import Store from './Store';
import { Entity } from './interfaces/Entity';
import { Loading, endLoading } from './interfaces/Loading';
import { Pagination, SetLimit, SetOffset } from './interfaces/Pagination';

type InterruptReasonIndexResponse = JrpcResponse<{
  items: ShutdownReasonInterface[];
  total: number;
}>;

class ShutdownReasonStore extends Store
  implements Loading, Entity<ShutdownReason, { limit?: number }>, Pagination {
  @observable private _shutdownReasons: ShutdownReason[];
  @observable private _limit: number;
  @observable private _offset: number;
  @observable private _total: number;
  @observable private _loading: boolean;

  @action private _endLoading = endLoading(200).bind(this);

  public constructor(services: Services) {
    super(services);

    this._shutdownReasons = [];
    this._limit = 20;
    this._offset = 0;
    this._total = 0;
    this._loading = false;
  }

  @action public load: LoadShutdownReason = async params => {
    let shutdownReasons: ShutdownReason[] = [];

    this._loading = true;

    const limit = params && typeof params.limit ? params.limit : this._limit;
    const offset = this._offset;

    await this._services.opencity.requests
      .referenceInterruptReasonIndex({ params: { limit, offset } })
      .then(({ data: { result } }: AxiosResponse<InterruptReasonIndexResponse>) => {
        if (result) {
          const { items, total } = result;

          if (Array.isArray(items)) {
            shutdownReasons = items.map<ShutdownReason>(value => new ShutdownReason(value));

            this._shutdownReasons = shutdownReasons;
          }

          if (typeof total === 'number') {
            this._total = total;
          }
        }
      })
      .finally(this._endLoading);

    return shutdownReasons;
  };

  @action public setLimit: SetLimit = limit => {
    this._limit = limit;
  };

  @action public setOffset: SetOffset = offset => {
    this._offset = offset;
  };

  @action public cleanUp = (): void => {
    this._shutdownReasons = [];
    this._limit = 20;
    this._offset = 0;
    this._total = 0;
    this._loading = false;
  };

  @computed public get list(): ShutdownReason[] {
    return toJS<ShutdownReason[]>(this._shutdownReasons);
  }

  @computed public get limit(): number {
    return this._limit;
  }

  @computed public get offset(): number {
    return this._offset;
  }

  @computed public get total(): number {
    return this._total;
  }

  @computed public get loading(): boolean {
    return this._loading;
  }
}

export default ShutdownReasonStore;
